import { useState, useMemo, useEffect, useRef } from "react";
import {
  Select,
  Stack,
  Button,
  Group,
  Paper,
  Alert,
  Text,
  Box,
  Collapse,
  Loader,
  Modal,
} from "@mantine/core";
import { DateInput, TimePicker } from "@mantine/dates";
import {
  IconChevronDown,
  IconChevronUp,
  IconAlertCircle,
} from "@tabler/icons-react";
import moment from "moment-timezone";
import "./AppointmentForm.css";
import { useSalon } from "../../../../../context/SalonContext";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useClient } from "../../../../../context/ClientContext";
import { useServiceAdjustmentForms } from "../../../../../context/ServiceAdjustmentFormsContext";
import { useApp } from "../../../../../context/AppContext";
import ClientLookup from "../../../../common/ClientLookup";
import AdditionalQuestionsSection from "../TransactionDetailModal/TransactionBookedServiceModal/AdditionalQuestionsSection";
import AddCardModal from "../../../../common/AddCardModal";
import { generateTimePresets } from "../../../../../utils/timePresets";
import { isCardValidForDate } from "../../../../../types/cardOnFile";
import {
  calculateDurationAndEndTime,
  calculateEndTime,
  handleSubmitHelper,
  hourOptions,
  generateDurationOptions,
} from "../../../../../utils/appointmentFormUtils";
import { filterBookableActiveStaff } from "../../../../../utils/staffUtils";

function AppointmentForm({ initialData, onSubmit, isLoading = false }) {
  const { employees, services, businessInfo } = useSalon();
  const { staffServices, selectedDate } = useCalendar();
  const { clientsById, refreshClientById } = useClient();
  const { adjustmentForms, formJunctions } = useServiceAdjustmentForms();
  const { getClientPaymentMethods } = useApp();
  const [availableServices, setAvailableServices] = useState(services);

  const [availableStaff, setAvailableStaff] = useState(() =>
    filterBookableActiveStaff(employees, initialData?.employeeId),
  );

  // State for service adjustment form options
  const [selectedAdjustmentOptions, setSelectedAdjustmentOptions] = useState(
    [],
  );
  const [isAdjustmentSectionExpanded, setIsAdjustmentSectionExpanded] =
    useState(false);

  const [showAddCardModal, setShowAddCardModal] = useState(false);
  /** Increment after a card is saved so payment methods are re-fetched and the alert can clear */
  const [cofPaymentMethodsRecheck, setCofPaymentMethodsRecheck] = useState(0);
  const [cofConfirmationOpened, setCofConfirmationOpened] = useState(false);
  const pendingAppointmentDataRef = useRef(null);

  // Generate time presets for TimePicker
  const timePresets = generateTimePresets();

  // Update available staff when employees load or change
  useEffect(() => {
    if (!formData.salonServiceId) {
      // Only update if no service is selected (service selection has its own filtering)
      setAvailableStaff(
        filterBookableActiveStaff(employees, formData.employeeId),
      );
    }
  }, [employees]);

  const mapInitialDataToFormData = (initialData) => {
    // Check if initialData is a SlotInfo object (from calendar slot selection)
    const isSlotInfo =
      initialData?.start instanceof Date && initialData?.action;

    let startTime = "";
    let endTime = "";
    let date = selectedDate;
    let employeeId = "";

    if (isSlotInfo) {
      // Extract time and date from SlotInfo
      startTime = moment(initialData.start).format("HH:mm");
      date = initialData.start;
      employeeId = initialData.resourceId || "";
    } else {
      // Use initialData properties for other cases
      startTime = initialData?.startTime || "";
      endTime = initialData?.endTime || "";
      date = initialData?.selectedDate || selectedDate;
      employeeId = initialData?.employeeId || "";
    }

    return {
      salonServiceId: initialData?.salonServiceId || "",
      employeeId: employeeId,
      startTime: startTime,
      endTime: endTime,
      date: date,
      baseDuration: initialData?.baseDuration ?? 30,
      processingTime: initialData?.processingTime ?? 0,
      finishingTime: initialData?.finishingTime ?? 0,
      bufferTime: initialData?.bufferTime ?? 0,
      clientUuid: initialData?.clientUuid || "",
    };
  };

  const [formData, setFormData] = useState(
    mapInitialDataToFormData(initialData),
  );

  useEffect(() => {
    setFormData(mapInitialDataToFormData(initialData));
  }, [initialData]);

  // Clear a selected service that no longer exists in the catalog (deleted then
  // dropped by refreshServices) so the operator re-picks instead of resubmitting.
  useEffect(() => {
    if (
      formData.salonServiceId &&
      services &&
      !services.some((s) => s.id === formData.salonServiceId)
    ) {
      setFormData((prev) => ({ ...prev, salonServiceId: "" }));
    }
  }, [services, formData.salonServiceId]);

  // Per-service Card-on-File requirement (migrated from salon-wide toggle)
  const selectedService = useMemo(
    () => services.find((s) => s.id === formData.salonServiceId),
    [services, formData.salonServiceId],
  );
  const requiresCardToBook = !!selectedService?.require_card_to_book;
  const [isCheckingCardOnFile, setIsCheckingCardOnFile] = useState(false);
  const [hasValidCardOnFile, setHasValidCardOnFile] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!requiresCardToBook || !formData.clientUuid || !formData.date) {
        setHasValidCardOnFile(null);
        return;
      }

      if (
        !businessInfo?.stripe_connect_account_id ||
        !businessInfo?.stripe_connect_account_is_onboarded
      ) {
        setHasValidCardOnFile(false);
        setIsCheckingCardOnFile(false);
        return;
      }

      const appointmentDate =
        formData.date instanceof Date ? formData.date : new Date(formData.date);

      if (Number.isNaN(appointmentDate.getTime())) {
        setHasValidCardOnFile(null);
        return;
      }

      setIsCheckingCardOnFile(true);
      try {
        const response = await getClientPaymentMethods(formData.clientUuid);
        const paymentMethods = response?.paymentMethods || [];
        const hasValid = paymentMethods.some((card) =>
          isCardValidForDate(card, appointmentDate),
        );

        if (!cancelled) {
          setHasValidCardOnFile(hasValid);
        }
      } catch (err) {
        console.error("Error checking client card on file:", err);
        if (!cancelled) {
          setHasValidCardOnFile(null);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingCardOnFile(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    requiresCardToBook,
    formData.clientUuid,
    formData.date,
    getClientPaymentMethods,
    cofPaymentMethodsRecheck,
    businessInfo?.stripe_connect_account_id,
    businessInfo?.stripe_connect_account_is_onboarded,
  ]);

  // Filter services when form initializes with a pre-selected staff member
  useEffect(() => {
    if (formData.employeeId && staffServices && services) {
      const staffServiceIds =
        staffServices
          .filter((service) => service.staffId === formData.employeeId)
          ?.map((service) => service.salonServiceId) || [];

      const filteredServices = services.filter((service) =>
        staffServiceIds.includes(service.id),
      );

      setAvailableServices(filteredServices);
    } else if (services) {
      // Reset to all services if no staff selected
      setAvailableServices(services);
    }
  }, [formData.employeeId, staffServices, services]);

  // Filter staff when form initializes with a pre-selected service
  useEffect(() => {
    if (formData.salonServiceId && services) {
      const selectedService = services.find(
        (s) => s.id === formData.salonServiceId,
      );

      if (selectedService) {
        const availableStaffIds =
          selectedService.servicing_staff?.map((staff) => staff.staff_id) || [];
        const filteredStaff = filterBookableActiveStaff(
          employees,
          formData.employeeId,
        ).filter((emp) => availableStaffIds.includes(emp.id));

        setAvailableStaff(filteredStaff);
      }
    } else {
      // Reset to active staff (plus currently selected) if no service selected
      setAvailableStaff(
        filterBookableActiveStaff(employees, formData.employeeId),
      );
    }
  }, [formData.salonServiceId, formData.employeeId, services, employees]);

  // Compute active adjustment forms for the selected service
  const activeAdjustmentForms = useMemo(() => {
    if (!formData.salonServiceId || !adjustmentForms || !formJunctions) {
      return [];
    }

    // Find junctions for this service
    const serviceJunctions = formJunctions.filter(
      (junction) => junction.salon_service_id === formData.salonServiceId,
    );

    if (serviceJunctions.length === 0) {
      return [];
    }

    // Get the form IDs from junctions
    const formIds = serviceJunctions.map((j) => j.service_adjustment_form_id);

    // Filter and sort forms
    const activeForms = adjustmentForms
      .filter(
        (form) =>
          form.is_active && formIds.includes(form.service_adjustment_form_id),
      )
      .sort((a, b) => a.priority - b.priority) // Lower priority = higher up
      .map((form) => ({
        ...form,
        questions: form.questions
          .slice() // Create a copy to avoid mutating original
          .sort((a, b) => a.priority - b.priority)
          .map((question) => ({
            ...question,
            options: question.options
              .slice()
              .sort((a, b) => a.priority - b.priority),
          })),
      }));

    return activeForms;
  }, [formData.salonServiceId, adjustmentForms, formJunctions]);

  // Calculate price and duration deltas when adjustment options change
  useEffect(() => {
    if (
      !formData.salonServiceId ||
      !formData.employeeId ||
      selectedAdjustmentOptions.length === 0
    ) {
      return;
    }

    // Calculate the total deltas from all selected options
    let totalPriceDelta = 0;
    let totalDurationDelta = 0;

    activeAdjustmentForms.forEach((form) => {
      form.questions.forEach((question) => {
        question.options.forEach((option) => {
          if (selectedAdjustmentOptions.includes(option.option_id)) {
            totalPriceDelta += option.price_delta || 0;
            totalDurationDelta += option.duration_delta_mins || 0;
          }
        });
      });
    });

    // Update form data with the adjusted values
    setFormData((prev) => {
      // Get base values from service/staff
      const selectedService = services.find(
        (s) => s.id === prev.salonServiceId,
      );
      if (!selectedService) return prev;

      const staffService = selectedService.servicing_staff?.find(
        (ss) => ss.staff_id === prev.employeeId,
      );

      const basePrice =
        staffService?.price || selectedService.default_price || 0;
      const baseDuration =
        staffService?.base_duration_mins ??
        selectedService.default_base_duration_mins ??
        30;

      // Apply deltas
      const newBaseDuration = baseDuration + totalDurationDelta;

      // Recalculate end time with new duration
      let newEndTime = prev.endTime;
      if (prev.startTime) {
        const totalMinutes =
          newBaseDuration +
          (prev.processingTime || 0) +
          (prev.finishingTime || 0) +
          (prev.bufferTime || 0);
        newEndTime = moment(prev.startTime, "HH:mm")
          .add(totalMinutes, "minutes")
          .format("HH:mm");
      }

      return {
        ...prev,
        baseDuration: newBaseDuration,
        endTime: newEndTime,
        // Note: We don't update price in formData since it's not stored there,
        // but the price delta will be applied during submission via the option IDs
      };
    });
  }, [
    selectedAdjustmentOptions,
    formData.salonServiceId,
    formData.employeeId,
    activeAdjustmentForms,
    services,
  ]);

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    employeeId: "",
    salonServiceId: "",
    clientUuid: "",
  });

  const [isDurationSectionExpanded, setIsDurationSectionExpanded] =
    useState(false);

  // Controlled dropdown state for start time picker
  const [startTimeDropdownOpened, setStartTimeDropdownOpened] = useState(false);
  // Track if change came from a mouse click (preset) vs keyboard (manual typing)
  const startTimeMouseDownRef = useRef(false);

  const handleStartTimeChange = (value) => {
    setFormData((currentFormData) => {
      const newFormData = { ...currentFormData, startTime: value };

      // If we have both service and staff selected, calculate new durations and end time
      if (currentFormData.salonServiceId && currentFormData.employeeId) {
        const calculated = calculateDurationAndEndTime(
          services,
          currentFormData.employeeId,
          currentFormData.salonServiceId,
          value,
          currentFormData,
        );

        if (calculated) {
          newFormData.baseDuration = calculated.baseDuration;
          newFormData.processingTime = calculated.processingTime;
          newFormData.finishingTime = calculated.finishingTime;
          newFormData.bufferTime = calculated.bufferTime;
          newFormData.endTime = calculated.endTime;
        }
      }

      return newFormData;
    });
  };

  const handleStaffSelect = (staffId) => {
    if (staffId) {
      const staffServiceIds =
        staffServices
          .filter((service) => service.staffId === staffId)
          ?.map((service) => service.salonServiceId) || [];

      const filteredServices = services.filter((service) =>
        staffServiceIds.includes(service.id),
      );

      setAvailableServices(filteredServices);
      setErrors((prev) => ({ ...prev, employeeId: "" }));

      // Update staff and durations in a single state update
      setFormData((prev) => {
        const newFormData = { ...prev, employeeId: staffId };

        // Update duration if a service is already selected
        if (prev.salonServiceId) {
          const calculated = calculateDurationAndEndTime(
            services,
            staffId,
            prev.salonServiceId,
            prev.startTime,
            prev,
          );

          if (calculated) {
            newFormData.baseDuration = calculated.baseDuration;
            newFormData.processingTime = calculated.processingTime;
            newFormData.finishingTime = calculated.finishingTime;
            newFormData.bufferTime = calculated.bufferTime;
            newFormData.endTime = calculated.endTime;
          }
        }

        return newFormData;
      });
    } else {
      // Reset to all services if no staff selected
      setFormData((prev) => ({ ...prev, employeeId: staffId }));
      setAvailableServices(services);
    }
  };

  const handleServiceSelect = (serviceId) => {
    // Reset adjustment options when service changes
    setSelectedAdjustmentOptions([]);
    setIsAdjustmentSectionExpanded(false);

    if (serviceId) {
      const selectedService = services.find((s) => s.id === serviceId);

      if (selectedService) {
        const availableStaffIds =
          selectedService.servicing_staff?.map((staff) => staff.staff_id) || [];
        const filteredStaff = filterBookableActiveStaff(
          employees,
          formData.employeeId,
        ).filter((emp) => availableStaffIds.includes(emp.id));

        setAvailableStaff(filteredStaff);
        setErrors((prev) => ({ ...prev, salonServiceId: "" }));

        // Update service and durations in a single state update
        setFormData((prev) => {
          const newFormData = { ...prev, salonServiceId: serviceId };

          // Update duration and end time if a staff member is already selected
          if (prev.employeeId) {
            const calculated = calculateDurationAndEndTime(
              services,
              prev.employeeId,
              serviceId,
              prev.startTime,
              prev,
            );

            if (calculated) {
              newFormData.baseDuration = calculated.baseDuration;
              newFormData.processingTime = calculated.processingTime;
              newFormData.finishingTime = calculated.finishingTime;
              newFormData.bufferTime = calculated.bufferTime;
              newFormData.endTime = calculated.endTime;
            }
          } else {
            // If no staff member selected yet, use service defaults
            const baseDuration =
              selectedService.default_base_duration_mins ?? 30;
            const processingTime =
              selectedService.default_processing_time_mins ?? 0;
            const finishingTime =
              selectedService.default_finishing_time_mins ?? 0;
            const bufferTime = selectedService.default_buffer_time_mins ?? 0;

            const totalMinutes =
              baseDuration + processingTime + finishingTime + bufferTime;
            const newEndTime = prev.startTime
              ? moment(prev.startTime, "HH:mm")
                  .add(totalMinutes, "minutes")
                  .format("HH:mm")
              : "";

            newFormData.baseDuration = baseDuration;
            newFormData.processingTime = processingTime;
            newFormData.finishingTime = finishingTime;
            newFormData.bufferTime = bufferTime;
            newFormData.endTime = newEndTime;
          }

          return newFormData;
        });
      }
    } else {
      // Reset to active staff (plus currently selected) if no service selected
      setFormData((prev) => ({ ...prev, salonServiceId: serviceId }));
      setAvailableStaff(
        filterBookableActiveStaff(employees, formData.employeeId),
      );
    }
  };

  const handleDurationChange = (field, value) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [field]: parseInt(value) || 0 };

      // Recalculate end time based on new durations
      if (prev.startTime) {
        const totalMinutes =
          (field === "baseDuration"
            ? parseInt(value) || 0
            : prev.baseDuration) +
          (field === "processingTime"
            ? parseInt(value) || 0
            : prev.processingTime) +
          (field === "finishingTime"
            ? parseInt(value) || 0
            : prev.finishingTime) +
          (field === "bufferTime" ? parseInt(value) || 0 : prev.bufferTime);

        newFormData.endTime = moment(prev.startTime, "HH:mm")
          .add(totalMinutes, "minutes")
          .format("HH:mm");
      }

      return newFormData;
    });
  };

  const handleAdjustmentOptionToggle = (optionId, questionId) => {
    setSelectedAdjustmentOptions((prev) => {
      // Find all options for this question across all forms
      const optionsForQuestion = [];
      activeAdjustmentForms.forEach((form) => {
        form.questions.forEach((question) => {
          if (question.question_id === questionId) {
            question.options.forEach((option) => {
              if (option.option_id) {
                optionsForQuestion.push(option.option_id);
              }
            });
          }
        });
      });

      // Remove any previously selected option from this question
      const withoutCurrentQuestion = prev.filter(
        (id) => !optionsForQuestion.includes(id),
      );

      // If optionId is empty, this is a deselection - don't add anything back
      if (!optionId) {
        return withoutCurrentQuestion;
      }

      // Add the newly selected option
      return [...withoutCurrentQuestion, optionId];
    });
  };

  const validateForm = () => {
    const newErrors = {
      employeeId: !formData.employeeId ? "Staff is required" : "",
      salonServiceId: !formData.salonServiceId ? "Service is required" : "",
      startTime: !formData.startTime ? "Start time is required" : "",
      clientUuid: !formData.clientUuid ? "Client is required" : "",
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const appointmentData = handleSubmitHelper(
      formData,
      staffServices,
      services,
      selectedAdjustmentOptions,
      businessInfo?.timezone,
    );

    // If this service requires card-on-file and client has none, confirm next step
    if (
      requiresCardToBook &&
      !isCheckingCardOnFile &&
      hasValidCardOnFile === false
    ) {
      pendingAppointmentDataRef.current = appointmentData;
      setCofConfirmationOpened(true);
      return;
    }

    onSubmit(appointmentData);
  };

  return (
    <Paper px="xs">
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <DateInput
            label="Date"
            required
            value={formData.date}
            onChange={(value) => setFormData({ ...formData, date: value })}
            clearable
            placeholder="Select date"
          />

          <ClientLookup
            value={formData.clientUuid}
            onChange={(value) => {
              setFormData({ ...formData, clientUuid: value });
              if (value) {
                setErrors({ ...errors, clientUuid: "" });
              }
            }}
            error={errors.clientUuid}
          />

          <Select
            label="Staff"
            required
            searchable
            nothingFoundMessage="No staff found"
            placeholder="Select staff member"
            data={availableStaff?.map((emp) => ({
              value: emp.id,
              label: `${emp.first_name} ${emp.last_name}`,
            }))}
            value={formData.employeeId}
            onChange={handleStaffSelect}
            error={errors.employeeId}
            styles={(theme) => ({
              input: {
                "&:focus": {
                  borderColor: theme.colors.teal[5],
                },
              },
            })}
          />

          <Select
            label="Service"
            required
            searchable
            nothingFoundMessage="No services found"
            placeholder="Select service"
            data={availableServices?.map((service) => ({
              value: service.id,
              label: `${service.name} - $${service.default_price}`,
            }))}
            value={formData.salonServiceId}
            onChange={handleServiceSelect}
            error={errors.salonServiceId}
            styles={(theme) => ({
              input: {
                "&:focus": {
                  borderColor: theme.colors.teal[5],
                },
              },
            })}
          />

          {/* Service Adjustment Forms (Additional Questions) */}
          <AdditionalQuestionsSection
            serviceId={formData.salonServiceId}
            activeAdjustmentForms={activeAdjustmentForms}
            selectedOptions={selectedAdjustmentOptions}
            onToggleOption={handleAdjustmentOptionToggle}
            isExpanded={isAdjustmentSectionExpanded}
            onToggleExpanded={() =>
              setIsAdjustmentSectionExpanded(!isAdjustmentSectionExpanded)
            }
          />

          <Group align="end" gap="xs">
            <Box
              onClickCapture={() => setStartTimeDropdownOpened(true)}
              onMouseDownCapture={() => {
                startTimeMouseDownRef.current = true;
              }}
            >
              <TimePicker
                placeholder="Start Time"
                label="Schedule"
                value={formData.startTime}
                onChange={(value) => {
                  handleStartTimeChange(value);
                  if (startTimeMouseDownRef.current) {
                    setStartTimeDropdownOpened(false);
                  }
                  startTimeMouseDownRef.current = false;
                }}
                onFocus={() => setStartTimeDropdownOpened(true)}
                format="12h"
                withSeconds={false}
                minutesStep={5}
                presets={timePresets}
                withDropdown={true}
                popoverProps={{
                  opened: startTimeDropdownOpened,
                  onChange: (_opened) =>
                    !_opened && setStartTimeDropdownOpened(false),
                }}
                maxDropdownContentHeight={300}
                required
                styles={(theme) => ({
                  input: {
                    fontSize: "1rem",
                    "&:focus": {
                      borderColor: theme.colors.teal[5],
                    },
                  },
                })}
              />
            </Box>
            <Text pb="4px">to</Text>
            <TimePicker
              placeholder="End Time"
              value={formData.endTime}
              format="12h"
              withSeconds={false}
              minutesStep={5}
              presets={timePresets}
              withDropdown={true}
              maxDropdownContentHeight={300}
              disabled
              styles={(theme) => ({
                input: {
                  fontSize: "1rem",
                  "&:focus": {
                    borderColor: theme.colors.teal[5],
                  },
                },
              })}
            />
          </Group>

          <Box>
            <Group
              onClick={() =>
                setIsDurationSectionExpanded(!isDurationSectionExpanded)
              }
              style={{ cursor: "pointer" }}
              mb="sm"
            >
              <Text c="#666" fw={500}>
                Adjust Service Durations
              </Text>
              {isDurationSectionExpanded ? (
                <IconChevronUp size={20} color="#666" />
              ) : (
                <IconChevronDown size={20} color="#666" />
              )}
            </Group>
            <Collapse in={isDurationSectionExpanded}>
              <Stack gap="sm">
                <Group gap="sm" align="end">
                  <Select
                    placeholder="Base"
                    label="Base Duration"
                    data={generateDurationOptions()}
                    value={formData.baseDuration.toString()}
                    onChange={(value) =>
                      handleDurationChange("baseDuration", value)
                    }
                    style={{ flex: 1 }}
                    styles={(theme) => ({
                      input: {
                        "&:focus": {
                          borderColor: theme.colors.teal[5],
                        },
                      },
                    })}
                  />
                  <Select
                    placeholder="Processing"
                    label="Processing Time"
                    data={[
                      { value: "0", label: "None" },
                      ...generateDurationOptions(),
                    ]}
                    value={formData.processingTime.toString()}
                    onChange={(value) =>
                      handleDurationChange("processingTime", value)
                    }
                    style={{ flex: 1 }}
                    styles={(theme) => ({
                      input: {
                        "&:focus": {
                          borderColor: theme.colors.teal[5],
                        },
                      },
                    })}
                  />
                </Group>
                <Group gap="sm" align="end">
                  <Select
                    placeholder="Finishing"
                    label="Finishing Time"
                    data={[
                      { value: "0", label: "None" },
                      ...generateDurationOptions(),
                    ]}
                    value={formData.finishingTime.toString()}
                    onChange={(value) =>
                      handleDurationChange("finishingTime", value)
                    }
                    style={{ flex: 1 }}
                    styles={(theme) => ({
                      input: {
                        "&:focus": {
                          borderColor: theme.colors.teal[5],
                        },
                      },
                    })}
                  />
                  <Select
                    placeholder="Buffer"
                    label="Buffer Time"
                    data={[
                      { value: "0", label: "None" },
                      ...generateDurationOptions(),
                    ]}
                    value={formData.bufferTime.toString()}
                    onChange={(value) =>
                      handleDurationChange("bufferTime", value)
                    }
                    style={{ flex: 1 }}
                    styles={(theme) => ({
                      input: {
                        "&:focus": {
                          borderColor: theme.colors.teal[5],
                        },
                      },
                    })}
                  />
                </Group>
              </Stack>
            </Collapse>
          </Box>

          <Button
            type="submit"
            fullWidth
            size="md"
            color="teal"
            mt="xs"
            mb="xs"
            disabled={isLoading}
            leftSection={isLoading ? <Loader size="xs" color="white" /> : null}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </form>

      {formData.clientUuid && (
        <AddCardModal
          opened={showAddCardModal}
          onClose={() => setShowAddCardModal(false)}
          clientId={formData.clientUuid}
          salonId={businessInfo?.id || ""}
          stripeAccountId={businessInfo?.stripe_connect_account_id || ""}
          clientName={
            clientsById[formData.clientUuid]
              ? `${clientsById[formData.clientUuid].first_name} ${clientsById[formData.clientUuid].last_name}`
              : undefined
          }
          onSuccess={async () => {
            setShowAddCardModal(false);
            pendingAppointmentDataRef.current = null;
            try {
              await refreshClientById(formData.clientUuid);
            } catch (e) {
              console.error("Error refreshing client after add card:", e);
            } finally {
              setCofPaymentMethodsRecheck((n) => n + 1);
            }
          }}
        />
      )}

      <Modal
        opened={cofConfirmationOpened}
        onClose={() => setCofConfirmationOpened(false)}
        centered
        title="Card on file required"
      >
        <Stack gap="sm">
          <Text>
            {selectedService?.name || "This service"} requires clients to have a
            card-on-file to book. Do you want to add one now?
          </Text>

          <Group justify="space-between" mt="xs">
            <Button
              variant="default"
              color="gray"
              onClick={() => {
                setCofConfirmationOpened(false);
                const pending = pendingAppointmentDataRef.current;
                pendingAppointmentDataRef.current = null;
                if (pending) onSubmit(pending);
              }}
            >
              Skip for now
            </Button>
            <Button
              color="teal"
              onClick={() => {
                setCofConfirmationOpened(false);
                setShowAddCardModal(true);
              }}
            >
              Add Card
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}

export default AppointmentForm;
